import { Check, Checks, Clock, IconProps, Pencil, WarningCircle, XCircle } from "@phosphor-icons/react";
import { Button, Color, Size, Variant } from "../../../components/Buttons";
import { PageTitle } from "../../../components/PageTitle";
import { Action, Header, Table } from "../../../components/Table";
import { useShipmentController } from "../../../controllers/shipmentsController";
import { Shipment, ShipmentStatus } from "../../../models/Shipment";
import { TopView } from "./styles";
import { UserRole } from "../../../models/User";
import { Avatar } from "../../../components/Avatar";
import { IconButton, Shape } from "../../../components/Buttons/IconButton";
import { ShipmentModal } from "../../../components/Modals/ShipmentModal";
import { format } from "date-fns";
import { buildInlineFileURL } from "../../../utils/fileProxy";

const statusIconProps: IconProps = {
  size: 24,
};

const statusIcon: {
  [ key in ShipmentStatus ]: React.ReactNode;
} = {
  [ ShipmentStatus.PENDING ]: <Clock { ...statusIconProps }/>,
  [ ShipmentStatus.SCHEDULED ]: <Clock { ...statusIconProps }/>,
  [ ShipmentStatus.SENT ]: <Check { ...statusIconProps }/>,
  [ ShipmentStatus.DELIVERED ]: <Checks { ...statusIconProps }/>,
  [ ShipmentStatus.FAILED ]: <WarningCircle { ...statusIconProps }/>,
  [ ShipmentStatus.CANCELED ]: <XCircle { ...statusIconProps }/>,
};

const statusColor: {
  [ key in ShipmentStatus ]: Color;
} = {
  [ ShipmentStatus.PENDING ]: "warning",
  [ ShipmentStatus.SCHEDULED ]: "warning",
  [ ShipmentStatus.SENT ]: "info",
  [ ShipmentStatus.DELIVERED ]: "success",
  [ ShipmentStatus.FAILED ]: "error",
  [ ShipmentStatus.CANCELED ]: "text",
};

const headers: Array<Header<Shipment>> = [
  {
    label: "",
    key: "contact",
    maxPossibleWidth: false,
    roles: [ UserRole.ADMIN, UserRole.MANAGER ],
    component: ( shipment: Shipment ) => (
      <Avatar
        name={ shipment.contact.name }
        size={ 32 }
        url={ buildInlineFileURL( shipment.contact.avatar )}
      />
    ),
  },
  {
    label: "Contato",
    key: "contact",
    roles: [ UserRole.ADMIN, UserRole.MANAGER, UserRole.ATTENDANT ],
    nested: ( row ) => row.contact.name,
  },
  {
    label: "Título",
    key: "title",
    roles: [ UserRole.ADMIN, UserRole.MANAGER, UserRole.ATTENDANT ],
  },
  {
    label: "Data de envio",
    key: "sendDate",
    transform: ( value ) => format( value, "dd/MM/yyyy"),
    roles: [ UserRole.ADMIN, UserRole.MANAGER, UserRole.ATTENDANT ],
  },
  {
    label: "Hora de envio",
    key: "sendTime",
    roles: [ UserRole.ADMIN, UserRole.MANAGER, UserRole.ATTENDANT ],
  },
  {
    label: "Status",
    key: "status",
    align: "center",
    maxPossibleWidth: false,
    roles: [ UserRole.ADMIN, UserRole.MANAGER ],
    component: ( value ) => (
      <IconButton
        color={ statusColor[ value.status ] as Color }
        shape={ Shape.CIRCLE }
      >
        { statusIcon[ value.status ]}
      </IconButton>
    ),
  }
];

export const Shipments = () => {
  const {
    page,
    perPage,
    total,
    search,
    shipments,
    loadingShipments,
    shipment,
    openShipmentModal,
    changePage,
    changeSearch,
    handleShipment,
    handleToggleOpenShipmentModal,
  } = useShipmentController();

  const actions: Array<Action<Shipment>> = [
    {
      icon: Pencil,
      label: "Editar",
      color: "text",
      onClick: handleShipment,
    },
  ];

  return (
    <>
      <TopView>
        <PageTitle>Envios Agendados</PageTitle>

        <Button
          $variant={ Variant.CONTAINED }
          $color="text"
          $size={ Size.MEDIUM }
          onClick={ handleToggleOpenShipmentModal }
        >
          Adicionar
        </Button>
      </TopView>

      <Table<Shipment>
        keyProperty="id"
        changePage={ changePage }
        page={ page }
        perPage={ perPage }
        total={ total }
        loading={ loadingShipments }
        data={ shipments }
        headers={ headers }
        actions={ actions }
        search={{
          value: search,
          placeholder: "Buscar por contato ou título",
          onChange: changeSearch,
        }}
      />

      <ShipmentModal
        open={ openShipmentModal }
        onClose={ handleToggleOpenShipmentModal }
        shipment={ shipment }
      />
    </>
  );
};
